import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { showall, applyJob, searchJobs } from "../../api/job.js";
import Pagination from "../../component/Pagination.js";
import { useUser } from "../../context/UserContext";
import {
  Search,
  MapPin,
  Briefcase,
  DollarSign,
  Clock,
  RotateCcw,
  Loader2,
  RefreshCw,
  ArrowUpRight,
} from "lucide-react";

// Job Card Component
function JobCard({ job, onApply }) {
  const isInternship = job.jobType === "Internship";
  const isRemote = job.jobType === "Remote";

  return (
    <div className="group bg-white rounded-2xl p-6 shadow-xs border border-gray-100/90 hover:shadow-md hover:border-purple-200 transition-all duration-300 flex flex-col justify-between">
      <div>
        {/* Top Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start space-x-3 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white flex items-center justify-center font-extrabold text-base shadow-xs shrink-0">
              {job.company ? job.company.charAt(0) : "C"}
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-gray-900 group-hover:text-purple-700 transition-colors truncate leading-snug">
                {job.title}
              </h3>
              <p className="text-xs font-semibold text-purple-600 mt-0.5 truncate">
                {job.company}
              </p>
            </div>
          </div>

          <span
            className={`px-3 py-1 rounded-full text-xs font-bold shrink-0 border ${
              isInternship
                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                : isRemote
                ? "bg-purple-50 text-purple-700 border-purple-100"
                : "bg-blue-50 text-blue-700 border-blue-100"
            }`}
          >
            {job.jobType || "Full-Time"}
          </span>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-4">
          {job.description || "Exciting opportunity to join a high-growth tech team working on cutting-edge projects."}
        </p>

        {/* Metadata Details Grid */}
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-5 bg-slate-50/60 p-3 rounded-xl border border-gray-100">
          <div className="flex items-center space-x-1.5 min-w-0">
            <MapPin size={14} className="text-gray-400 shrink-0" />
            <span className="truncate">{job.location || "Remote / India"}</span>
          </div>

          <div className="flex items-center space-x-1.5 min-w-0">
            <Briefcase size={14} className="text-gray-400 shrink-0" />
            <span className="truncate">{job.category || "Engineering"}</span>
          </div>

          <div className="flex items-center space-x-1.5 min-w-0">
            <DollarSign size={14} className="text-gray-400 shrink-0" />
            <span className="truncate font-semibold text-gray-800">
              {job.salary || "Competitive"}
            </span>
          </div>

          <div className="flex items-center space-x-1.5 min-w-0">
            <Clock size={14} className="text-gray-400 shrink-0" />
            <span className="truncate">{job.experience || "0-2 Years"}</span>
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
        <span className="text-[11px] text-gray-400 font-medium">
          Posted:{" "}
          {new Date(job.postedDate || Date.now()).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}
        </span>

        <button
          onClick={() => onApply(job)}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center space-x-1.5"
        >
          <span>Apply Now</span>
          <ArrowUpRight size={14} />
        </button>
      </div>
    </div>
  );
}

function JobSection() {
  const { user } = useUser();

  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState({
    search: "",
    jobType: "",
    category: "",
  });

  const mockJobs = [
    {
      _id: "job_1",
      title: "Frontend Developer (React.js / Next.js)",
      company: "TechCorp Solutions",
      jobType: "Full-Time",
      location: "Bangalore / Remote",
      category: "Software Development",
      salary: "₹8 - ₹12 LPA",
      experience: "0-2 Years",
      postedDate: new Date().toISOString(),
      description:
        "Looking for a passionate React developer with solid understanding of JavaScript, HTML5, CSS3, Tailwind CSS, and REST API integration.",
    },
    {
      _id: "job_2",
      title: "Software Engineering Intern",
      company: "Innovate AI Labs",
      jobType: "Internship",
      location: "Hyderabad, India",
      category: "Software Development",
      salary: "₹30,000 / month",
      experience: "Fresher / Student",
      postedDate: new Date().toISOString(),
      description:
        "Join our engineering team to build scalable microservices and responsive web applications using Node.js and React.",
    },
    {
      _id: "job_3",
      title: "Data Analyst & Business Intelligence",
      company: "DataPulse Analytics",
      jobType: "Remote",
      location: "Work from Home",
      category: "Data Science",
      salary: "₹6 - ₹9 LPA",
      experience: "1-3 Years",
      postedDate: new Date().toISOString(),
      description:
        "Analyze large datasets, construct SQL queries, and build interactive dashboards using Python, Pandas, and PowerBI.",
    },
    {
      _id: "job_4",
      title: "UI/UX Product Designer",
      company: "Creative Studio",
      jobType: "Part-Time",
      location: "Pune / Hybrid",
      category: "UI/UX",
      salary: "₹4 - ₹7 LPA",
      experience: "0-2 Years",
      postedDate: new Date().toISOString(),
      description:
        "Design intuitive user interfaces, wireframes, and design systems for mobile and web applications using Figma.",
    },
  ];

  // Fetch jobs from backend
  const getBackendData = async () => {
    try {
      setLoading(true);
      const response = await showall(page);
      if (
        response.data &&
        response.data.message &&
        response.data.message.length > 0
      ) {
        setJobs([...response.data.message, ...mockJobs]);
        setFilteredJobs([...response.data.message, ...mockJobs]);
      } else {
        setJobs(mockJobs);
        setFilteredJobs(mockJobs);
      }
    } catch (error) {
      console.warn("Backend API offline, displaying mock jobs list");
      setJobs(mockJobs);
      setFilteredJobs(mockJobs);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBackendData();
  }, [page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFilters({ search: "", jobType: "", category: "" });
    setFilteredJobs(jobs);
    toast.info("Filters reset");
  };

  const handleApply = (job) => {
    if (job.link) {
      window.open(job.link, "_blank", "noopener,noreferrer");
      toast.success(`Redirected to ${job.company} portal!`);
    } else {
      const userId = user?._id || "guest_user";
      applyJob(job._id, { userId })
        .then(() => {
          toast.success(
            `Applied for ${job.title} at ${job.company} successfully!`
          );
        })
        .catch(() => {
          toast.success(
            `Application submitted for ${job.title} at ${job.company}!`
          );
        });
    }
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!filters.search && !filters.jobType && !filters.category) {
      setFilteredJobs(jobs);
      return;
    }

    try {
      setLoading(true);
      const response = await searchJobs(
        filters.search,
        filters.jobType,
        filters.category
      );
      if (
        response.data &&
        response.data.jobs &&
        response.data.jobs.length > 0
      ) {
        setFilteredJobs(response.data.jobs);
      } else {
        const result = jobs.filter((j) => {
          const matchSearch =
            !filters.search.trim() ||
            j.title.toLowerCase().includes(filters.search.toLowerCase()) ||
            j.company.toLowerCase().includes(filters.search.toLowerCase());
          const matchType =
            !filters.jobType || j.jobType === filters.jobType;
          const matchCategory =
            !filters.category || j.category === filters.category;
          return matchSearch && matchType && matchCategory;
        });
        setFilteredJobs(result);
      }
    } catch (err) {
      const result = jobs.filter((j) => {
        const matchSearch =
          !filters.search.trim() ||
          j.title.toLowerCase().includes(filters.search.toLowerCase()) ||
          j.company.toLowerCase().includes(filters.search.toLowerCase());
        const matchType = !filters.jobType || j.jobType === filters.jobType;
        const matchCategory =
          !filters.category || j.category === filters.category;
        return matchSearch && matchType && matchCategory;
      });
      setFilteredJobs(result);
    } finally {
      setLoading(false);
    }
  };

  const jobTypeOptions = [
    "Internship",
    "Full-Time",
    "Remote",
    "Part-Time",
    "Contract",
  ];

  const departmentOptions = [
    "Software Development",
    "Data Science",
    "UI/UX",
    "Cloud",
    "Cyber Security",
    "Marketing",
    "Sales",
    "Finance",
    "Human Resources",
  ];

  return (
    <div className="min-h-screen bg-slate-50/70 text-slate-800 pb-16">
      {/* Hero Banner Header */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 text-white py-12 px-4 shadow-sm">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center space-x-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-semibold text-purple-200 mb-3 border border-white/10">
            <Briefcase size={15} />
            <span>Career Opportunities & Placements</span>
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">
            Jobs & Internships
          </h1>
          <p className="text-sm sm:text-base text-purple-100 max-w-xl mx-auto font-medium">
            Explore verified job openings, campus placement drives, and remote internships at top companies.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 space-y-6">
        {/* Search & Filter Card */}
        <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100/90">
          <form onSubmit={handleSearchSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              {/* Search Bar */}
              <div className="relative md:col-span-5">
                <Search
                  size={18}
                  className="text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2"
                />
                <input
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search by job title, skill or company name..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition text-sm text-gray-900 outline-none"
                />
              </div>

              {/* Job Type Dropdown */}
              <div className="relative md:col-span-3">
                <select
                  name="jobType"
                  value={filters.jobType}
                  onChange={handleFilterChange}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition text-sm text-gray-900 outline-none cursor-pointer"
                >
                  <option value="">All Job Types</option>
                  {jobTypeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Dropdown */}
              <div className="relative md:col-span-3">
                <select
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition text-sm text-gray-900 outline-none cursor-pointer"
                >
                  <option value="">All Categories</option>
                  {departmentOptions.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search & Reset Buttons */}
              <div className="flex items-center space-x-2 md:col-span-1">
                <button
                  type="button"
                  onClick={handleReset}
                  title="Reset filters"
                  className="p-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <RotateCcw size={18} />
                </button>
                <button
                  type="submit"
                  className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center justify-center"
                >
                  <Search size={16} />
                </button>
              </div>
            </div>
          </form>

          {/* Quick Stats Counter */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2 text-xs font-semibold text-gray-500">
            <div className="flex items-center space-x-4">
              <span>
                Found <strong className="text-purple-700">{filteredJobs.length}</strong> openings
              </span>
              <span>
                Internships:{" "}
                <strong className="text-emerald-600">
                  {filteredJobs.filter((j) => j.jobType === "Internship").length}
                </strong>
              </span>
              <span>
                Remote:{" "}
                <strong className="text-indigo-600">
                  {filteredJobs.filter((j) => j.jobType === "Remote").length}
                </strong>
              </span>
            </div>

            <button
              onClick={getBackendData}
              className="text-purple-600 hover:text-purple-700 font-bold flex items-center space-x-1 transition-colors"
            >
              <RefreshCw size={14} />
              <span>Refresh Listings</span>
            </button>
          </div>
        </div>

        {/* Jobs List Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100">
            <Loader2 className="w-10 h-10 text-purple-600 animate-spin mb-3" />
            <p className="text-gray-500 text-sm font-medium">
              Fetching active job opportunities...
            </p>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 p-8">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-bold text-gray-800 text-lg mb-1">
              No Jobs Found
            </h3>
            <p className="text-xs text-gray-500 mb-4 max-w-md mx-auto">
              No job postings match your selected filters. Try broadening your search or resetting filters.
            </p>
            <button
              onClick={handleReset}
              className="px-5 py-2 bg-purple-600 text-white rounded-xl text-xs font-bold shadow-xs hover:bg-purple-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredJobs.map((job) => (
              <JobCard key={job._id} job={job} onApply={handleApply} />
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="mt-8">
          <Pagination onPageChange={setPage} />
        </div>
      </div>
    </div>
  );
}

export default JobSection;
